package com.dignicate.web.domain

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.flatMapMerge
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.stateIn


@Suppress("unused")
private class Logger {
    fun d(message: String) {
        console.log("[DEBUG] $message")
    }

    fun e(throwable: Throwable, message: String) {
        console.error("[ERROR] $message", throwable)
    }
}

sealed class Resource<out T: Any> {
    data object Initialized : Resource<Nothing>()
    data object InProgress : Resource<Nothing>()
    data class Success<out T: Any>(val data: T) : Resource<T>()
    data class Failure<out T: Any>(val error: Error) : Resource<T>()
}

sealed interface Cursor<out T : Any> {
    data object First : Cursor<Nothing>

    data class Next<out T : Any>(val value: T) : Cursor<T>

    data object End : Cursor<Nothing>

    @Suppress("unused")
    val cursor: T?
        get() = when (this) {
            First, End -> null
            is Next -> value
        }

    @Suppress("unused")
    val isFirst: Boolean
        get() = when (this) {
            First -> true
            is Next -> false
            End -> false
        }
}

data class ResourceWithCursor<out R : Any, out C : Any>(
    val currentCursor: Cursor<C>,
    val nextCursor: Cursor<C>?,
    val resource: Resource<R>,
)

data class ParamWithCursor<out P : Any, out C : Any>(
    val cursor: Cursor<C>,
    val param: P,
)

fun <S: Any, T: Any> MutableSharedFlow<S>.mapToResource(
    scope: CoroutineScope,
    then: (S) -> Flow<Result<T>>,
    mapError: (Throwable) -> Error = { Error.Unknown(it) },
): StateFlow<Resource<T>> {
    console.log("[mapToResource] START - scope:", scope)
    val flowResult = mapToResourceAsFlow(then, mapError)
    console.log("[mapToResource] mapToResourceAsFlow created, calling stateIn")
    return flowResult.stateIn(
        scope = scope,
        started = SharingStarted.Eagerly,
        initialValue = Resource.Initialized,
    ).also {
        console.log("[mapToResource] StateFlow created with initial value:", it.value)
    }
}

@OptIn(ExperimentalCoroutinesApi::class)
fun <S: Any, T: Any> MutableSharedFlow<S>.mapToResourceAsFlow(
    then: (S) -> Flow<Result<T>>,
    mapError: (Throwable) -> Error = { Error.Unknown(it) },
): Flow<Resource<T>> {
    console.log("[mapToResourceAsFlow] START")
    return flatMapMerge { param: S ->
        console.log("[mapToResourceAsFlow] flatMapMerge - param received:", param)
        flow {
            console.log("[mapToResourceAsFlow] flow block - emitting InProgress")
            emit(Resource.InProgress)
            try {
                console.log("[mapToResourceAsFlow] calling then(param)")
                then(param)
                    .collect { result: Result<T> ->
                        console.log("[mapToResourceAsFlow] result received:", result)
                        result.fold(
                            onSuccess = { data: T ->
                                console.log("[mapToResourceAsFlow] Success - data:", data)
                                emit(
                                    Resource.Success(
                                        data
                                    )
                                )
                            },
                            onFailure = { throwable: Throwable ->
                                console.log("[mapToResourceAsFlow] Failure:", throwable)
                                emit(
                                    Resource.Failure(
                                        mapError(throwable)
                                    )
                                )
                            }
                        )
                    }
            } catch (t: Throwable) {
                console.log("[mapToResourceAsFlow] Exception caught:", t)
                emit(
                    Resource.Failure(
                        mapError(t)
                    )
                )
            }

        }
    }
}

sealed class Error {
    abstract val message: String

    data class Unknown(val throwable: Throwable) : Error() {
        override val message: String
            get() = throwable.message ?: throwable.toString()
    }
}

@OptIn(ExperimentalCoroutinesApi::class)
fun <S : Any, T : Any, U : Any> Flow<ParamWithCursor<S, U>>.mapToResourceFlow(
    then: (ParamWithCursor<S, U>) -> Flow<Result<T>>,
    nextCursor: (S, T, Cursor<U>) -> Cursor<U>,
    onSuccess: (S, T, Cursor<U>) -> T = { _, data, _ -> data },
    mapError: ((Throwable) -> Error)? = null,
): Flow<ResourceWithCursor<T, U>> {
    return flatMapMerge { param: ParamWithCursor<S, U> ->
        flow {
            emit(
                ResourceWithCursor(
                    currentCursor = param.cursor,
                    nextCursor = null,
                    resource = Resource.InProgress
                )
            )
            then(param).collect { result: Result<T> ->
                result.fold(
                    onSuccess = { data: T ->
                        emit(
                            ResourceWithCursor(
                                currentCursor = param.cursor,
                                nextCursor = nextCursor(param.param, data, param.cursor),
                                resource = Resource.Success(
                                    onSuccess(param.param, data, param.cursor)
                                ),
                            )
                        )
                    },
                    onFailure = { exception ->
                        val error = mapError?.invoke(exception) ?: Error.Unknown(exception)
                        emit(
                            ResourceWithCursor(
                                currentCursor = param.cursor,
                                nextCursor = null,
                                resource = Resource.Failure(
                                    error
                                ),
                            )
                        )
                    }
                )
            }
        }
    }
}

@Suppress("unused")
fun <S : Any, T : Any, U : Any> Flow<ParamWithCursor<S, U>>.mapToResource(
    scope: CoroutineScope,
    then: (ParamWithCursor<S, U>) -> Flow<Result<T>>,
    nextCursor: (S, T, Cursor<U>) -> Cursor<U>,
    onSuccess: (S, T, Cursor<U>) -> T = { _, data, _ -> data },
    mapError: ((Throwable) -> Error)? = null,
): StateFlow<ResourceWithCursor<T, U>> {
    return mapToResourceFlow(
        then = then,
        nextCursor = nextCursor,
        onSuccess = onSuccess,
        mapError = mapError,
    )
        .stateIn(
            scope = scope,
            started = SharingStarted.WhileSubscribed(),
            initialValue = ResourceWithCursor(
                Cursor.First,
                null,
                Resource.Initialized
            ),
        )
}

@OptIn(ExperimentalCoroutinesApi::class)
fun <S : Any, T : Any, U : Any> Flow<S>.concatMapToResourceFlow(
    then1: (S) -> Flow<Result<T>>,
    then2: (S, T) -> Flow<Result<U>>,
    mapError: ((Throwable) -> Error)? = null,
): Flow<Resource<U>> {
    return flatMapMerge { param: S ->
        flow {
            emit(Resource.InProgress)
            then1(param).collect { result1 ->
                if (result1.isSuccess) {
                    val intermediateResult = result1.getOrThrow()
                    then2(param, intermediateResult).collect { result2 ->
                        if (result2.isSuccess) {
                            emit(Resource.Success(result2.getOrThrow()))
                        } else {
                            throw result2.exceptionOrNull() ?: Exception("Unknown error")
                        }
                    }
                } else {
                    throw result1.exceptionOrNull() ?: Exception("Unknown error")
                }
            }
        }.catch { exception ->
            val error = mapError?.invoke(exception) ?: Error.Unknown(exception)
            emit(Resource.Failure(error))
        }
    }
}

@Suppress("unused")
fun <S : Any, T : Any, U : Any> Flow<S>.concatMapToResource(
    scope: CoroutineScope,
    then1: (S) -> Flow<Result<T>>,
    then2: (S, T) -> Flow<Result<U>>,
    mapError: ((Throwable) -> Error)? = null,
): StateFlow<Resource<U>> {
    return concatMapToResourceFlow(
        then1 = then1,
        then2 = then2,
        mapError = mapError,
    )
        .stateIn(
            scope = scope,
            started = SharingStarted.WhileSubscribed(),
            initialValue = Resource.Initialized,
        )
}
